// src/pages/SectionDetails.jsx
import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useDarkMode, DM } from "../context/DarkModeContext";
import { SECTIONS, SECTION_FIELDS } from "../constants/sections";
import { loadAllSections } from "../utils/sectionUtils";
import { api } from "../utils/api";
import DashboardHeader from "../components/DashboardHeader";
import { AddItemModal, EditEntryModal, ImportModal } from "./FacultyDashboard";

export default function SectionDetails() {
  const { sectionKey } = useParams();
  const [searchParams] = useSearchParams();
  const { currentUser, logout, userRole } = useAuth();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const d = darkMode;
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allSections, setAllSections] = useState(SECTIONS);
  const [submittedReports, setSubmittedReports] = useState([]);
  const [toast, setToast] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showEditEntryModal, setShowEditEntryModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // If admin is viewing a specific faculty's section, facultyId is in the query param
  const facultyId = searchParams.get("facultyId");
  const isAdmin = userRole === "admin" || userRole === "superadmin";
  // Use the faculty's ID if provided (admin viewing), otherwise current user
  const targetUserId = facultyId || currentUser?.uid;

  useEffect(() => {
    if (currentUser) {
      fetchProfile();
      fetchSubmittedReports();
    }
    loadAllSections().then(setAllSections);
  }, [currentUser, targetUserId]);

  async function fetchProfile() {
    try {
      const res = await api.get(`/faculty/${targetUserId}/profile`);
      let profileData = { id: targetUserId, ...res };
      
      // Fetch section data
      try {
        const sectionRes = await api.get(`/faculty/${targetUserId}/section/${sectionKey}`);
        let sectionData = Array.isArray(sectionRes) ? sectionRes : [];
        profileData[sectionKey] = sectionData.map(row => ({
          id: row.id,
          ...(typeof row.data === 'string' ? JSON.parse(row.data) : row.data || row)
        }));
      } catch (err) {
        console.warn(`Failed to fetch ${sectionKey}:`, err);
        profileData[sectionKey] = [];
      }
      
      setProfile(profileData);
      console.log('targetUserId:', targetUserId);
      console.log('sectionKey:', sectionKey);
      console.log('Profile data:', profileData);
      console.log('Section entries:', profileData[sectionKey]);
    } catch (err) {
      console.error("Failed to fetch profile:", err);
    }
    setLoading(false);
  }

  async function fetchSubmittedReports() {
    try {
      const res = await api.get(`/submitted_reports?orderBy=submittedAt&desc=true`);
      const reports = Array.isArray(res) ? res : [];
      setSubmittedReports(reports
        .filter(r => r.facultyId === targetUserId && r.sectionKey === sectionKey)
      );
    } catch (err) { console.error(err); }
  }

  function checkIsSubmitted(item, index) {
    if (!item) return false;
    // Check if any report contains this item. 
    // We use addedAt or updatedAt as a unique-ish key, or deep equality.
    return submittedReports.some(r => 
      (r.submittedIndexes && r.submittedIndexes.includes(index)) || // fallback to index
      (r.reportData && r.reportData.some(ri => 
        (ri.addedAt && ri.addedAt === item.addedAt) || 
        JSON.stringify(ri) === JSON.stringify({ ...item, siNo: ri.siNo })
      ))
    );
  }

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(""), 3500); }

  async function saveEditEntry(oldItem, newItem) {
    try {
      await api.put(`/faculty/${targetUserId}/section/${sectionKey}/${oldItem.id}`, {
        ...newItem,
        updatedAt: new Date().toISOString()
      });
      fetchProfile();
      showToast("✅ Entry updated successfully!");
    } catch { showToast("❌ Failed to update"); }
  }

  async function deleteEntry(item, index) {
      const isSubmitted = checkIsSubmitted(item, index);

      if (isSubmitted && !isAdmin) {
          alert("This entry is already submitted. Only Admin can delete it.");
          return;
      }

      if (!window.confirm("Are you sure you want to delete this entry?")) return;

      try {
          await api.delete(`/faculty/${targetUserId}/section/${sectionKey}/${item.id}`);
          fetchProfile();
          showToast("✅ Entry deleted successfully!");
      } catch (err) {
          console.error("Delete error:", err);
          showToast("❌ Failed to delete");
      }
  }

  if (loading) return <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>⏳ Loading...</div>;

  const sectionData = allSections.find(s => s.key === sectionKey);
  const entries = profile?.[sectionKey] || [];
  // Where to go back: admin goes back to the faculty profile, faculty goes to dashboard
  const backUrl = facultyId ? `/admin/faculty/${facultyId}` : "/dashboard";
  const backLabel = facultyId ? "← Back to Faculty Profile" : "← Back to Dashboard";

  return (
    <div style={{ minHeight: "100vh", background: DM.pageBg(d), fontFamily: "Georgia, serif", display: "flex", flexDirection: "column" }}>
      <DashboardHeader label="Section Details" showNewsTicker={true}>
        <button onClick={() => navigate(backUrl)} style={{ background: "#fff", color: "#1d4ed8", border: "none", borderRadius: 10, padding: "7px 14px", fontSize: 15, fontWeight: 800, cursor: "pointer" }}>{backLabel}</button>
        {!facultyId && <button onClick={() => navigate("/my-report")} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 10, padding: "7px 14px", fontSize: 15, cursor: "pointer" }}>📊 My Report</button>}
      </DashboardHeader>
      
      {toast && (
        <div style={{ position: "fixed", top: 80, right: 24, zIndex: 999, background: toast.startsWith("✅") ? "#f0fdf4" : "#fef2f2", color: toast.startsWith("✅") ? "#16a34a" : "#dc2626", borderRadius: 12, padding: "14px 20px", fontWeight: 700, fontSize: 16, border: `1px solid ${toast.startsWith("✅") ? "#86efac" : "#fecaca"}` }}>
          {toast}
        </div>
      )}

      <main style={{ flex: 1, padding: "32px", maxWidth: 1000, margin: "0 auto", width: "100%" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 900, color: "#0f2557", margin: 0 }}>{sectionData?.icon} {sectionData?.label}</h1>
            {facultyId && profile && (
              <p style={{ color: "#7c3aed", margin: "4px 0 0", fontSize: 15, fontWeight: 700 }}>👑 Admin View: {profile.name}</p>
            )}
            <p style={{ color: "#64748b", margin: "4px 0 0" }}>Manage {sectionData?.label.toLowerCase()} entries here.</p>
          </div>
          {/* Only show Add button for own entries or for admin viewing a faculty */}
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={() => setShowAddModal(true)} style={{ background: sectionData?.color, color: "#fff", border: "none", borderRadius: 12, padding: "10px 24px", fontWeight: 700, cursor: "pointer" }}>+ Add New</button>
            {sectionKey === "articlePublications" && (
              <button onClick={() => setShowImportModal(true)} style={{ background: "#8b5cf6", color: "#fff", border: "none", borderRadius: 12, padding: "10px 24px", fontWeight: 700, cursor: "pointer" }}>📥 Import Excel</button>
            )}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {entries.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px", background: "#fff", borderRadius: 20, border: "1px solid #f1f5f9" }}>
                <div style={{ fontSize: 50, marginBottom: 16 }}>{sectionData?.icon}</div>
                <p style={{ color: "#94a3b8" }}>No entries found for {sectionData?.label}.</p>
                <button onClick={() => setShowAddModal(true)} style={{ marginTop: 16, background: sectionData?.light, color: sectionData?.color, border: "none", borderRadius: 10, padding: "10px 24px", fontWeight: 700, cursor: "pointer" }}>Add Entry</button>
            </div>
          ) : (
            entries.map((item, i) => (
              <DetailCard 
                key={i} 
                item={item} 
                index={i} 
                section={sectionKey} 
                sectionData={sectionData} 
                isSubmitted={checkIsSubmitted(item, i)}
                onEdit={() => { setEditingItem(item); setShowEditEntryModal(true); }}
                onDelete={() => deleteEntry(item, i)}
                userRole={userRole}
              />
            ))
          )}
        </div>
      </main>

      {showAddModal && <AddItemModal section={sectionKey} sectionData={sectionData} userId={targetUserId} onClose={() => setShowAddModal(false)} onSuccess={() => { setShowAddModal(false); fetchProfile(); showToast("✅ Added successfully!"); }} />}
      {showImportModal && <ImportModal section={sectionKey} sectionData={sectionData} userId={targetUserId} onClose={() => setShowImportModal(false)} onSuccess={() => { setShowImportModal(false); fetchProfile(); showToast("✅ Import completed!"); }} />}
      {showEditEntryModal && editingItem && <EditEntryModal section={sectionKey} sectionData={sectionData} item={editingItem} userId={targetUserId} onClose={() => { setShowEditEntryModal(false); setEditingItem(null); }} onSuccess={(old, newItem) => { saveEditEntry(old, newItem); setShowEditEntryModal(false); setEditingItem(null); }} />}

      <style>{`
        body { margin: 0; padding: 0; }
      `}</style>
    </div>
  );
}

function DetailCard({ item, index, section, sectionData, isSubmitted, onEdit, onDelete, userRole }) {
  const [fields, setFields] = useState([]);
  const isAdmin = userRole === "admin" || userRole === "superadmin";

  useEffect(() => {
    async function loadFields() {
      try {
        const res = await api.get(`/section_fields_${section}?orderBy=order`);
        const fieldsData = Array.isArray(res) ? res : [];
        setFields(fieldsData.length > 0 ? fieldsData : (SECTION_FIELDS[section] || []));
      } catch (err) {
        console.error("Failed to load fields in DetailCard:", err);
        setFields(SECTION_FIELDS[section] || []);
      }
    }
    loadFields();
  }, [section]);

  function getKey(f) { return f.key || f.name?.toLowerCase().replace(/[^a-z0-9]/g, "_"); }
  const titleField = fields.find(f => f.required);
  const title = titleField ? item[getKey(titleField)] : Object.values(item).find(v => v && typeof v === "string" && v !== "addedAt" && v !== "updatedAt");

  return (
    <div style={{ background: "#fff", borderRadius: 16, padding: "20px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9", borderLeft: `5px solid ${sectionData?.color}` }}>
      <div style={{ display: "flex", gap: 16 }}>
        <div style={{ width: 32, height: 32, borderRadius: "50%", background: sectionData?.color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, flexShrink: 0 }}>{index + 1}</div>
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: "#1e293b", margin: 0 }}>{title || "Untitled Entry"}</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {isSubmitted && <span style={{ background: "#dcfce7", color: "#16a34a", fontSize: 13, fontWeight: 700, padding: "4px 10px", borderRadius: 20 }}>✅ Submitted</span>}
              <button onClick={onEdit} style={{ background: "#eff6ff", color: "#2563eb", border: "none", borderRadius: 8, padding: "6px 12px", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>✏️ Edit</button>
              {(isAdmin || !isSubmitted) && (
                <button onClick={onDelete} style={{ background: "#fef2f2", color: "#dc2626", border: "none", borderRadius: 8, padding: "6px 12px", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>🗑️ Delete</button>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px 20px" }}>
            {fields.map(f => {
              const k = getKey(f);
              const val = item[k];
              if (!val) return null;
              return (
                <div key={k}>
                  <div style={{ fontSize: 13, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase" }}>{f.label || f.name}</div>
                  <div style={{ fontSize: 15, color: "#475569", fontWeight: 600 }}>
                    {typeof val === "string" && val.startsWith("http") ? <a href={val} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none" }}>🔗 View File</a> : val}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Reusing Modals from FacultyDashboard or importing them would be better.
